import * as React from 'react';
import { Box, IconButton, styled } from '@mui/material';
import Done from '@mui/icons-material/Done';
import Close from '@mui/icons-material/Close';
import Download from '@mui/icons-material/Download';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplay } from 'lib/SettingDisplay';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { useDownloadAsset } from 'lib/hooks/useDownloadAsset';

const PREFIX = 'OrderToTableSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`,
    image: `${PREFIX}-image`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    },
    [`& .${classes.image}`]: {
        width: 'auto',
        height: 'auto',
        maxHeight: '400px',
        maxWidth: '100%'
    }
}));

type IProps = {
    settings: IPublicAndPrivateSettings;
    tenantId: string;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export const OrderToTableSettings = ({ settings, tenantId, onSubmit }: IProps) => {
    const { loading: ottSelectionImageLoading, handleDownload: handleDownloadOttSelectionImage } =
        useDownloadAsset({
            src: `${process.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/web_ott_selection`,
            fileName: 'web_ott_selection'
        });

    return (
        <StyledMuiCardForm
            title="Order to Table"
            initialValues={{
                orderToTableEnabled: settings?.orderToTableEnabled ?? false,
                limitLocationRange: settings?.orderToTable?.limitLocationRange ?? false,
                nfcEnabled: settings?.orderToTable?.nfcEnabled ?? false,
                multiPartEnabled: settings?.orderToTable?.multiPartEnabled ?? false,
                canChangeTableNumber: settings?.orderToTable?.canChangeTableNumber ?? true,
                ottCardImage: `${process.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/web_ott_selection`
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Order To Table Enabled">
                        {settings?.orderToTableEnabled && <Done />}
                        {!settings?.orderToTableEnabled && <Close />}
                    </SettingDisplay>
                    <SettingDisplay label="Multi-part Ordering">
                        {settings?.orderToTable?.multiPartEnabled && <Done />}
                        {!settings?.orderToTable?.multiPartEnabled && <Close />}
                    </SettingDisplay>
                    <SettingDisplay label="Limit Location Range">
                        {settings?.orderToTable?.limitLocationRange && <Done />}
                        {!settings?.orderToTable?.limitLocationRange && <Close />}
                    </SettingDisplay>
                    <SettingDisplay label="Order To Table NFC Enabled">
                        {settings?.orderToTable?.nfcEnabled && <Done />}
                        {!settings?.orderToTable?.nfcEnabled && <Close />}
                    </SettingDisplay>
                    <SettingDisplay label="Change Table Number Enabled">
                        {settings?.orderToTable?.canChangeTableNumber && <Done />}
                        {!settings?.orderToTable?.canChangeTableNumber && <Close />}
                    </SettingDisplay>
                    <SettingDisplay
                        label="Web Journey Selection Image"
                        description="Min Ratio 657px X 220px, Max Ratio 800px x 268px PNG or JPEG"
                    >
                        <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center" gap={1}>
                            <img
                                src={`${process.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/web_ott_selection`}
                                className={classes.image}
                            />
                            <IconButton
                                disabled={ottSelectionImageLoading}
                                onClick={handleDownloadOttSelectionImage}
                            >
                                <Download />
                            </IconButton>
                        </Box>
                    </SettingDisplay>
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Order To Table Enabled">
                        <SwitchFormField name="orderToTableEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Multi-part Ordering">
                        <SwitchFormField name="multiPartEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Limit Location Range">
                        <SwitchFormField name="limitLocationRange" />
                    </SettingDisplay>
                    <SettingDisplay label="Order To Table NFC Enabled">
                        <SwitchFormField name="nfcEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Change Table Number Enabled">
                        <SwitchFormField name="canChangeTableNumber" />
                    </SettingDisplay>
                    <SettingDisplay
                        label="Web Journey Selection Image"
                        description="Min Ratio 657px X 220px, Max Ratio 800px x 268px PNG or JPEG"
                    >
                        <ImageFormField
                            name="ottCardImage"
                            aspect={657 / 220}
                            fileName="web_ott_selection"
                            uploadFolder="app_assets"
                            unremovable
                        />
                    </SettingDisplay>
                </Box>
            }
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
};
